// React imports
import React, { useEffect, useRef, useState } from "react";

// External library imports
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";

// Local imports
import PageTitle from "../../../components/Page Component/PageTitle";
import { RootState } from "../../../store/Store";
import {
  fetchData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import { employeeKRAsUrl } from "../../../Service/Apis";
import {
  getStatusChip,
  formatDate,
  calculateWeightedRating,
  getPeriodShortLabel,
  getPeriodDaysWarningColor,
  formatRatingLabel,
} from "../utils";
import { EmployeeKRA } from "./Types/EmployeeSelfReviewTypes";
import { AxiosErrorResponse } from "./Types/ErrorTypes";
import RichTextDisplay from "../../../components/SkillDocumentCommentBox/RichTextDisplay";

const EmployeeSelfReviewDashboard: React.FC = () => {
  // 1. useState declarations
  const [myKRAs, setMyKRAs] = useState<EmployeeKRA[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedKRA, setSelectedKRA] = useState<EmployeeKRA | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);

  // 2. const variables (hooks, Redux, computed constants)
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();
  const showToast = useCustomToast();
  const navigate = useNavigate();
  const kraListRef = useRef<HTMLDivElement>(null);

  // 3. Function declarations
  const fetchMyKRAs = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await dispatch(fetchData(employeeKRAsUrl));

      if (response && Array.isArray(response)) {
        // Show all KRAs (including pending self-reviews)
        setMyKRAs(response);
      } else {
        setMyKRAs([]);
      }
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load your reviews";
      showToast(errorMessage, "error", true);
      setMyKRAs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (kra: EmployeeKRA): void => {
    setSelectedKRA(kra);
    setViewDialogOpen(true);
  };

  const calculateAverageRating = (kra: EmployeeKRA): number => {
    return calculateWeightedRating(
      kra.sections.map((section) => ({
        weightage: section.weightage,
        questions: section.questions.map((q) => ({
          weightage: q.weightage,
          rating_type: q.rating_type,
          self_rating: q.self_rating,
          manager_rating: null,
        })),
      })),
      "self_rating"
    );
  };

  const getRatingColor = (rating: number | null): string => {
    if (!rating) return "#9e9e9e";
    if (rating >= 4.5) return "#2e7d32";
    if (rating >= 4.0) return "#388e3c";
    if (rating >= 3.5) return "#689f38";
    if (rating >= 3.0) return "#fbc02d";
    if (rating >= 2.5) return "#f57c00";
    return "#d32f2f";
  };

  // 4. useEffect hooks (just above return)
  useEffect(() => {
    fetchMyKRAs();
  }, []);

  // Calculate statistics (computed constant)
  const stats = {
    total: myKRAs.length,
    pendingAcceptance: myKRAs.filter((kra) => kra.status === "pending").length,
    pendingSelfReview: myKRAs.filter(
      (kra) => kra.status === "accepted" && kra.self_review_submitted_date === null
    ).length,
    awaitingManager: myKRAs.filter(
      (kra) => kra.status === "awaiting_manager_review"
    ).length,
    awaitingHR: myKRAs.filter((kra) => kra.status === "awaiting_hr_review")
      .length,
    completed: myKRAs.filter((kra) => kra.status === "completed").length,
    avgRating:
      myKRAs.length > 0
        ? (
            myKRAs.reduce((sum, kra) => {
              const rating = calculateAverageRating(kra);
              return sum + rating;
            }, 0) / myKRAs.length
          ).toFixed(2)
        : "0.00",
  };

  // Returns a status chip for a KRA card.
  // When the KRA has two *different* managers and is awaiting manager review,
  // the label is refined to indicate which manager(s) still need to submit.
  const getDualManagerStatusChip = (kra: EmployeeKRA): JSX.Element => {
    if (
      kra.status === "awaiting_manager_review" &&
      kra.manager_1 &&
      kra.manager_2 &&
      kra.manager_1 !== kra.manager_2
    ) {
      const m1Submitted = !!kra.manager_1_review_submitted_date;
      const m2Submitted = !!kra.manager_2_review_submitted_date;

      let label: string;
      if (!m1Submitted && !m2Submitted) {
        label = "Awaiting Managers' Review";
      } else if (m1Submitted && !m2Submitted) {
        label = "Awaiting 2nd Manager's Review";
      } else if (!m1Submitted && m2Submitted) {
        label = "Awaiting 1st Manager's Review";
      } else {
        return getStatusChip(kra.status);
      }

      return (
        <Chip
          label={label}
          color="info"
          icon={<RateReviewIcon sx={{ fontSize: 16 }} />}
          size="small"
          sx={{ fontWeight: "600" }}
        />
      );
    }

    return getStatusChip(kra.status);
  };

  // Return JSX
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <PageTitle>📊 PMS Dashboard</PageTitle>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2} sx={{ display: "flex" }}>
          <Card elevation={2} sx={{ height: "100%", width: "100%", minHeight: 110 }}>
            <CardContent>
              <Typography color="black" variant="body2" gutterBottom>
                Total Reviews
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2} sx={{ display: "flex" }}>
          <Card
            elevation={2}
            onClick={() => navigate("/pms/kra/employee")}
            sx={{
              bgcolor: "#fff3e0",
              height: "100%",
              width: "100%",
              minHeight: 110,
              cursor: "pointer",
              "&:hover": { boxShadow: 6 },
            }}
          >
            <CardContent>
              <Typography color="black" variant="body2" gutterBottom>
                Pending KRA Acceptance
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "#f57c00" }}
              >
                {stats.pendingAcceptance}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2} sx={{ display: "flex" }}>
          <Card
            elevation={2}
            onClick={() => navigate("/pms/kra/employee")}
            sx={{
              bgcolor: "#e3f2fd",
              height: "100%",
              width: "100%",
              minHeight: 110,
              cursor: "pointer",
              "&:hover": { boxShadow: 6 },
            }}
          >
            <CardContent>
              <Typography color="black" variant="body2" gutterBottom>
                Pending Self-Review
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "#1976d2" }}
              >
                {stats.pendingSelfReview}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2} sx={{ display: "flex" }}>
          <Card elevation={2} sx={{ bgcolor: "#e8f5e9", height: "100%", width: "100%", minHeight: 110 }}>
            <CardContent>
              <Typography color="black" variant="body2" gutterBottom>
                Pending Manager Review
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "#2e7d32" }}
              >
                {stats.awaitingManager}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2} sx={{ display: "flex" }}>
          <Card elevation={2} sx={{ bgcolor: "#eeeeee", height: "100%", width: "100%", minHeight: 110 }}>
            <CardContent>
              <Typography color="black" variant="body2" gutterBottom>
                Pending HR Review
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "#616161" }}
              >
                {stats.awaitingHR}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2} sx={{ display: "flex" }}>
          <Card
            elevation={2}
            onClick={() => {
              if (kraListRef.current) {
                const top = kraListRef.current.getBoundingClientRect().top + window.scrollY - 90;
                window.scrollTo({ top, behavior: "smooth" });
              }
            }}
            sx={{
              bgcolor: "#fff9c4",
              height: "100%",
              width: "100%",
              minHeight: 110,
              cursor: "pointer",
              "&:hover": { boxShadow: 6 },
            }}
          >
            <CardContent>
              <Typography color="black" variant="body2" gutterBottom>
                Completed Reviews
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "#f9a825" }}
              >
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Reviews List */}
      <div ref={kraListRef} />
      {myKRAs.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: "center" }}>
          <AssessmentIcon sx={{ fontSize: 64, color: "action.disabled", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
            No KRA Assignments Found
          </Typography>
          <Typography variant="body2" color="black">
            You don't have any KRA assignments at the moment.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {myKRAs.map((kra) => {
            // Use self_review_overall_rating from API if available, otherwise calculate
            const avgRating = typeof kra.self_review_overall_rating === "number"
              ? kra.self_review_overall_rating
              : calculateAverageRating(kra);
            const percentage = (avgRating / 5) * 100;

            return (
              <Card key={kra.employee_kra_id} elevation={2} sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    {/* Left Section - KRA Info */}
                    <Grid item xs={12} md={8}>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "bold", mb: 0.5 }}
                        >
                          {kra.form_name}
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}
                        >
                          <Chip
                            label={kra.designation}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={getPeriodShortLabel(kra.review_period)}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                          {getDualManagerStatusChip(kra)}
                        </Box>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="black">
                            Assigned Date
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: "500" }}>
                            {formatDate(kra.assigned_date)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="black">
                            Submitted Date
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: "500" }}>
                            {formatDate(kra.self_review_submitted_date)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="black">
                            Review Period
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: "500" }}>
                            {formatDate(kra.period_start_date)} -{" "}
                            {formatDate(kra.period_end_date)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="black">
                            Days Remaining
                          </Typography>
                          <Chip
                            label={`${kra.days_remaining} days`}
                            size="small"
                            color={getPeriodDaysWarningColor(kra.days_remaining)}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Right Section - Rating */}
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 2,
                          bgcolor: "#f5f5f5",
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="caption" color="black">
                          Self-Rating
                        </Typography>
                        {kra.self_review_submitted_date ? (
                          <>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: "bold",
                                color: getRatingColor(avgRating),
                                my: 1,
                              }}
                            >
                              {avgRating.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="black" gutterBottom>
                              out of 5.00
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: "#e0e0e0",
                                "& .MuiLinearProgress-bar": {
                                  bgcolor: getRatingColor(avgRating),
                                },
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: "bold",
                                color: "#1976d2",
                                my: 1,
                              }}
                            >
                              —
                            </Typography>
                            <Typography variant="body2" color="black" gutterBottom>
                              Not yet submitted
                            </Typography>
                            <Chip
                              label="Pending"
                              size="small"
                              sx={{
                                bgcolor: "#e3f2fd",
                                color: "#1976d2",
                                fontWeight: "600",
                                mt: 2,
                              }}
                            />
                          </>
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleViewDetails(kra)}
                    fullWidth
                  >
                    View Detailed Review
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Detailed View Dialog */}
      {selectedKRA && (
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {selectedKRA.form_name}
              </Typography>
              <Typography variant="body2" color="black">
                Self-Review Details - Submitted on{" "}
                {formatDate(selectedKRA.self_review_submitted_date)}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {/* Overall Ratings & Comments - All Three Reviews */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                Overall Ratings & Comments
              </Typography>
              
              {/* Self-Review */}
              <Box sx={{ mb: 2, p: 2, bgcolor: "#e3f2fd", borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="black" sx={{ fontWeight: "bold" }}>
                      Self-Rating
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        color: selectedKRA.self_review_submitted_date
                          ? getRatingColor(
                              typeof selectedKRA.self_review_overall_rating === "number"
                                ? selectedKRA.self_review_overall_rating
                                : calculateAverageRating(selectedKRA)
                            )
                          : "black",
                      }}
                    >
                      {selectedKRA.self_review_submitted_date
                        ? (() => {
                            const rating = typeof selectedKRA.self_review_overall_rating === "number"
                              ? selectedKRA.self_review_overall_rating
                              : calculateAverageRating(selectedKRA);
                            return rating.toFixed(2);
                          })() + " / 5.00"
                        : "Pending"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Typography variant="caption" color="black" sx={{ fontWeight: "bold" }}>
                      Comments
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {selectedKRA.self_review_overall_comments ? (
                        <RichTextDisplay content={selectedKRA.self_review_overall_comments} />
                      ) : (
                        <Typography variant="body2" sx={{ color: "black" }}>
                          {selectedKRA.self_review_submitted_date ? "No comments provided" : "Pending"}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Manager Review — show M1 and M2 separately when dual managers exist */}
              {(() => {
                const hasDual = selectedKRA.manager_1 && selectedKRA.manager_2;

                const renderManagerBlock = (
                  label: string,
                  rating: number | null | undefined,
                  comments: string | null | undefined,
                  submittedDate: string | null | undefined,
                  bgcolor: string,
                ) => (
                  <Box sx={{ mb: hasDual ? 1.5 : 0, p: 2, bgcolor, borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="black" sx={{ fontWeight: "bold" }}>
                          {label}
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: "bold",
                            color: typeof rating === "number"
                              ? getRatingColor(rating)
                              : "black",
                          }}
                        >
                          {typeof rating === "number" ? rating.toFixed(2) + " / 5.00" : "Pending"}
                        </Typography>
                        {submittedDate && (
                          <Typography variant="caption" color="black" sx={{ display: "block", mt: 1 }}>
                            Submitted: {formatDate(submittedDate)}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Typography variant="caption" color="black" sx={{ fontWeight: "bold" }}>
                          Comments
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {comments ? (
                            <RichTextDisplay content={comments} />
                          ) : (
                            <Typography variant="body2" sx={{ color: "black" }}>
                              {submittedDate ? "No comments provided" : "Pending"}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );

                if (hasDual) {
                  return (
                    <>
                      {renderManagerBlock(
                        `${selectedKRA.manager_1_name || "Manager 1"} (M1) Rating`,
                        selectedKRA.manager_1_overall_rating,
                        selectedKRA.manager_1_overall_comments,
                        selectedKRA.manager_1_review_submitted_date,
                        "#e8f5e9",
                      )}
                      {renderManagerBlock(
                        `${selectedKRA.manager_2_name || "Manager 2"} (M2) Rating`,
                        selectedKRA.manager_2_overall_rating,
                        selectedKRA.manager_2_overall_comments,
                        selectedKRA.manager_2_review_submitted_date,
                        "#f3e5f5",
                      )}
                    </>
                  );
                }

                // Single manager fallback
                return renderManagerBlock(
                  `${selectedKRA.manager_1_name || selectedKRA.manager_2_name || "Manager"} Rating`,
                  selectedKRA.manager_overall_rating,
                  selectedKRA.manager_1_overall_comments || selectedKRA.manager_2_overall_comments || selectedKRA.manager_overall_comments,
                  selectedKRA.manager_1_review_submitted_date || selectedKRA.manager_2_review_submitted_date || selectedKRA.manager_review_submitted_date,
                  "#e8f5e9",
                );
              })()}

              {/* HR Review */}
              <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: "#eeeeee", borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="black" sx={{ fontWeight: "bold" }}>
                      HR Final Rating
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        color: typeof selectedKRA.hr_overall_rating === "number"
                          ? getRatingColor(selectedKRA.hr_overall_rating)
                          : "black",
                      }}
                    >
                      {typeof selectedKRA.hr_overall_rating === "number"
                        ? selectedKRA.hr_overall_rating.toFixed(2)
                        : "Pending"}{" "}
                      {typeof selectedKRA.hr_overall_rating === "number" && "/ 5.00"}
                    </Typography>
                    {selectedKRA.hr_review_date && (
                      <Typography variant="caption" color="black" sx={{ display: "block", mt: 1 }}>
                        Reviewed: {formatDate(selectedKRA.hr_review_date)}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Typography variant="caption" color="black" sx={{ fontWeight: "bold" }}>
                      HR Comments
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {selectedKRA.hr_overall_comments ? (
                        <RichTextDisplay content={selectedKRA.hr_overall_comments} />
                      ) : (
                        <Typography variant="body2" sx={{ color: "black" }}>
                          {selectedKRA.hr_review_date ? "No comments provided" : "Pending"}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Section-wise Ratings */}
            {selectedKRA.sections.map((section) => (
              <Accordion key={section.section_id} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", pr: 2 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {section.section}
                    </Typography>
                    <Chip
                      label={`Weightage: ${section.weightage}%`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      {(() => {
                        const hasDual = selectedKRA.manager_1 && selectedKRA.manager_2;
                        const m1Label = selectedKRA.manager_1_name ? `${selectedKRA.manager_1_name} (M1)` : "M1";
                        const m2Label = selectedKRA.manager_2_name ? `${selectedKRA.manager_2_name} (M2)` : "M2";

                        const ratingChip = (val: number | null | undefined, ratingType?: string) =>
                          val !== null && val !== undefined ? (
                            <Chip
                              label={typeof val === "number" ? formatRatingLabel(val, ratingType as any) : "Pending"}
                              size="small"
                              sx={{ bgcolor: getRatingColor(val), color: "white", fontWeight: "600" }}
                            />
                          ) : (
                            <Typography variant="body2">Pending</Typography>
                          );

                        return (
                          <>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold", width: "25%" }} rowSpan={2}>KPI / Question</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", width: "8%" }} rowSpan={2}>Weightage</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", bgcolor: "#e3f2fd" }} colSpan={2}>Self-Review</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", bgcolor: "#e8f5e9" }} colSpan={2}>
                                  {hasDual ? m1Label : `${selectedKRA.manager_1_name || selectedKRA.manager_2_name || "Manager"} Review`}
                                </TableCell>
                                {hasDual && (
                                  <TableCell align="center" sx={{ fontWeight: "bold", bgcolor: "#f3e5f5" }} colSpan={2}>{m2Label}</TableCell>
                                )}
                              </TableRow>
                              <TableRow>
                                <TableCell align="center" sx={{ fontWeight: "bold", width: "8%", bgcolor: "#e3f2fd" }}>Rating</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: "15%", bgcolor: "#e3f2fd" }}>Comments</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", width: "8%", bgcolor: "#e8f5e9" }}>Rating</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: "15%", bgcolor: "#e8f5e9" }}>Comments</TableCell>
                                {hasDual && <>
                                  <TableCell align="center" sx={{ fontWeight: "bold", width: "8%", bgcolor: "#f3e5f5" }}>Rating</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", width: "15%", bgcolor: "#f3e5f5" }}>Comments</TableCell>
                                </>}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {section.questions.map((kpi) => (
                                <TableRow key={kpi.kpi_id}>
                                  <TableCell>{kpi.question}</TableCell>
                                  <TableCell align="center">{kpi.weightage}%</TableCell>
                                  {/* Self */}
                                  <TableCell align="center" sx={{ bgcolor: "#f0f7ff" }}>
                                    {ratingChip(kpi.self_rating, kpi.rating_type)}
                                  </TableCell>
                                  <TableCell sx={{ bgcolor: "#f0f7ff" }}>
                                    {kpi.self_comments ? (
                                      <RichTextDisplay content={kpi.self_comments} sx={{ maxHeight: 220, fontSize: 13 }} />
                                    ) : (
                                      <Typography variant="body2">
                                        {selectedKRA.self_review_submitted_date ? "No comments" : "Pending"}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  {/* M1 — falls back to M2 data when only M2 is assigned */}
                                  <TableCell align="center" sx={{ bgcolor: "#f1f8f4" }}>
                                    {ratingChip(
                                      hasDual ? kpi.manager_rating : (kpi.manager_rating ?? kpi.manager_2_rating),
                                      kpi.rating_type
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ bgcolor: "#f1f8f4" }}>
                                    {(() => {
                                      const m1Comments = hasDual ? kpi.manager_comments : (kpi.manager_comments || kpi.manager_2_comments);
                                      return m1Comments ? (
                                        <RichTextDisplay content={m1Comments} sx={{ maxHeight: 220, fontSize: 13 }} />
                                      ) : (
                                        <Typography variant="body2">
                                          {selectedKRA.manager_1_review_submitted_date || selectedKRA.manager_2_review_submitted_date || selectedKRA.manager_review_submitted_date ? "No comments" : "Pending"}
                                        </Typography>
                                      );
                                    })()}
                                  </TableCell>
                                  {/* M2 — only shown when dual managers exist */}
                                  {hasDual && <>
                                    <TableCell align="center" sx={{ bgcolor: "#fdf4ff" }}>
                                      {ratingChip(kpi.manager_2_rating, kpi.rating_type)}
                                    </TableCell>
                                    <TableCell sx={{ bgcolor: "#fdf4ff" }}>
                                      {kpi.manager_2_comments ? (
                                        <RichTextDisplay content={kpi.manager_2_comments} sx={{ maxHeight: 220, fontSize: 13 }} />
                                      ) : (
                                        <Typography variant="body2">
                                          {selectedKRA.manager_2_review_submitted_date ? "No comments" : "Pending"}
                                        </Typography>
                                      )}
                                    </TableCell>
                                  </>}
                                </TableRow>
                              ))}
                            </TableBody>
                          </>
                        );
                      })()}
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default EmployeeSelfReviewDashboard;

